import {type TooltipFormulaItem} from '../../../../components/TooltipFormulaRenderer';
import {useIntl, type IntlShape} from 'react-intl';
import React from 'react';
import {FinancialCategoryTooltip} from '@/components/insights/FinancialCategoryTooltip';
import {PERIOD_BUDGET_TYPE} from '../../../../constants';

type FormatMessage = IntlShape['formatMessage'];

const isTask = (setting: string | null | undefined): boolean => {
	return setting?.toLowerCase() === 'task';
};

const NetRolloverTooltipContent = (formatMessage: FormatMessage, {inHours}): TooltipFormulaItem[] => {
	return [
		{
			title: formatMessage({id: 'retainer.net_rollover'}),
			description: formatMessage({
				id: inHours ? 'retainer.net_rollover_tooltip_description_hours' : 'retainer.net_rollover_tooltip_description',
			}),
			details: [
				formatMessage({id: 'retainer.rollover_to_period'}),
				'-',
				formatMessage({id: 'retainer.rollover_from_period'}),
			],
		},
	];
};

const PeriodPriceTargetTooltipContent = (formatMessage: FormatMessage, {budgetType}): TooltipFormulaItem[] => {
	const isFixedPrice = budgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const isFixedHours = budgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS;

	if (isFixedPrice) {
		return [
			{
				title: formatMessage({id: 'retainer.period_price'}),
				description: formatMessage({id: 'retainer.period_price_tooltip_description'}),
			},
		];
	} else if (isFixedHours) {
		return [
			{
				title: formatMessage({id: 'retainer.period_target'}),
				description: formatMessage({id: 'retainer.period_target_fixed_hours_tooltip_description'}),
			},
		];
	} else {
		return [
			{
				title: formatMessage({id: 'retainer.period_target'}),
				description: formatMessage({id: 'retainer.period_target_time_material_tooltip_description'}),
			},
		];
	}
};

const AdjustedPriceTooltipContent = (formatMessage: FormatMessage): TooltipFormulaItem[] => {
	return [
		{
			title: formatMessage({id: 'retainer.adjusted_price'}),
			description: formatMessage({id: 'retainer.adjusted_price_tooltip_description'}),
			details: [formatMessage({id: 'retainer.period_price'}), '+', formatMessage({id: 'retainer.net_rollover'})],
		},
	];
};

const AdjustedTargetTooltipContent = (formatMessage: FormatMessage): TooltipFormulaItem[] => {
	return [
		{
			title: formatMessage({id: 'retainer.adjusted_target'}),
			description: formatMessage({id: 'retainer.adjusted_target_tooltip_description'}),
			details: [formatMessage({id: 'retainer.period_target'}), '+', formatMessage({id: 'retainer.net_rollover'})],
		},
	];
};

const ActualBillableTooltipContent = (formatMessage: FormatMessage, {budgetType, actualHoursSetting}): TooltipFormulaItem[] => {
	const isFixedPrice = budgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const isTimeReg = isTask(actualHoursSetting); // Seems like we use task if time registration is selected in the interface

	if (isFixedPrice) {
		// Fixed Price: Actual Billable Value of Service
		if (isTimeReg) {
			return [
				{
					title: formatMessage({id: 'common.actual_billable_value_of_service'}),
					description: formatMessage({
						id: 'retainer.actual_billable_value_of_service_time_reg_tooltip_description',
					}),
					details: [
						formatMessage({id: 'project_budget.billable_hours_registered'}),
						'x',
						formatMessage({id: 'common.rate'}),
					],
				},
			];
		} else {
			// Allocation
			return [
				{
					title: formatMessage({id: 'common.actual_billable_value_of_service'}),
					description: formatMessage({
						id: 'retainer.actual_billable_value_of_service_allocation_tooltip_description',
					}),
					details: [formatMessage({id: 'retainer.past_allocated_hours'}), 'x', formatMessage({id: 'common.rate'})],
				},
			];
		}
	} else {
		// Fixed Hours or Time and Material: Actual Billable Hours
		if (isTimeReg) {
			return [
				{
					title: formatMessage({id: 'common.actual_billable_hours'}),
					description: formatMessage({
						id: 'retainer.actual_billable_hours_time_reg_tooltip_description',
					}),
					details: [
						formatMessage({id: 'project_budget.billable_hours_registered'}),
						'-',
						formatMessage({id: 'retainer.conflicted_billable_hours'}),
					],
				},
			];
		} else {
			// Allocation
			return [
				{
					title: formatMessage({id: 'common.actual_billable_hours'}),
					description: formatMessage({
						id: 'retainer.actual_billable_hours_allocation_tooltip_description',
					}),
					details: [formatMessage({id: 'retainer.past_allocated_hours'})],
				},
			];
		}
	}
};

const ActualBudgetRemainingTooltipContent = (formatMessage: FormatMessage, {budgetType, hasRollover}): TooltipFormulaItem[] => {
	const isFixedPrice = budgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const isFixedHours = budgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS;

	if (isFixedPrice) {
		// Fixed Price: Actual Budget Remaining
		const titleId = 'retainer.actual_budget_remaining';
		const descriptionId = 'retainer.actual_budget_remaining_tooltip_description';
		const details = hasRollover
			? [
					formatMessage({id: 'retainer.adjusted_price'}),
					'-',
					formatMessage({id: 'common.actual_billable_value_of_service'}),
			  ]
			: [
					formatMessage({id: 'retainer.period_price'}),
					'-',
					formatMessage({id: 'common.actual_billable_value_of_service'}),
			  ];

		return [
			{
				title: formatMessage({id: titleId}),
				description: formatMessage({id: descriptionId}),
				details,
			},
		];
	} else {
		// Fixed Hours or Time and Material: Actual Hours Budget Remaining
		const titleId = 'retainer.actual_budget_hours_remaining';
		const descriptionId = 'retainer.actual_budget_hours_remaining_tooltip_description';
		let details: (string | number)[];

		if (isFixedHours && hasRollover) {
			// Fixed Hours with rollover
			details = [
				formatMessage({id: 'retainer.adjusted_target'}),
				'-',
				formatMessage({id: 'common.actual_billable_hours'}),
			];
		} else {
			// Fixed Hours without rollover or Time and Material
			details = [formatMessage({id: 'retainer.period_target'}), '-', formatMessage({id: 'common.actual_billable_hours'})];
		}

		return [
			{
				title: formatMessage({id: titleId}),
				description: formatMessage({id: descriptionId}),
				details,
			},
		];
	}
};

const ActualPercentageUsedTooltipContent = (formatMessage: FormatMessage, {budgetType, hasRollover}): TooltipFormulaItem[] => {
	const isFixedPrice = budgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const isFixedHours = budgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS;

	if (isFixedPrice) {
		// Fixed Price: Actual % Budget Used
		const titleId = 'retainer.actual_percentage_budget_used';
		const descriptionId = 'retainer.actual_percentage_budget_used_tooltip_description';
		const details = hasRollover
			? [
					formatMessage({id: 'common.actual_billable_value_of_service'}),
					'/',
					formatMessage({id: 'retainer.adjusted_price'}),
			  ]
			: [
					formatMessage({id: 'common.actual_billable_value_of_service'}),
					'/',
					formatMessage({id: 'retainer.period_price'}),
			  ];

		return [
			{
				title: formatMessage({id: titleId}),
				description: formatMessage({id: descriptionId}),
				details,
			},
		];
	} else {
		// Fixed Hours or Time and Material: Actual % Target Used
		const titleId = 'retainer.actual_percentage_target_used';
		const descriptionId = 'retainer.actual_percentage_target_used_tooltip_description';
		const details =
			isFixedHours && hasRollover
				? [formatMessage({id: 'common.actual_billable_hours'}), '/', formatMessage({id: 'retainer.adjusted_target'})]
				: [formatMessage({id: 'common.actual_billable_hours'}), '/', formatMessage({id: 'retainer.period_target'})];

		return [
			{
				title: formatMessage({id: titleId}),
				description: formatMessage({id: descriptionId}),
				details,
			},
		];
	}
};

const ProjectedTotalBillableTooltipContent = (formatMessage: FormatMessage, {inHours}): TooltipFormulaItem[] => {
	if (inHours) {
		// Fixed Hours or Time and Material: Projected Total Billable Hours
		return [
			{
				title: formatMessage({id: 'common.projected_total_hours'}),
				description: formatMessage({
					id: 'retainer.projected_total_billable_hours_tooltip_description',
				}),
				details: [
					formatMessage({id: 'common.actual_billable_hours'}),
					'+',
					formatMessage({id: 'common.remaining_work_billable_hours'}),
				],
			},
		];
	} else {
		// Fixed Price: Projected Total Billable Value of Service
		return [
			{
				title: formatMessage({id: 'common.projected_total_billable_value_of_service'}),
				description: formatMessage({
					id: 'retainer.projected_total_billable_value_of_service_tooltip_description',
				}),
				details: [
					formatMessage({id: 'common.actual_billable_value_of_service'}),
					'+',
					formatMessage({id: 'common.remaining_work_billable_value_of_service'}),
				],
			},
		];
	}
};

const ProjectedTotalBudgetRemainingTooltipContent = (
	formatMessage: FormatMessage,
	{budgetType, hasRollover}
): TooltipFormulaItem[] => {
	const isFixedPrice = budgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const isFixedHours = budgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS;

	if (isFixedPrice) {
		// Fixed Price: Projected Total Budget Remaining
		const titleId = 'common.projected_total_budget_remaining';
		const descriptionId = 'retainer.projected_total_budget_remaining_tooltip_description';
		const details = hasRollover
			? [
					formatMessage({id: 'retainer.adjusted_price'}),
					'-',
					formatMessage({id: 'common.projected_total_billable_value_of_service'}),
			  ]
			: [
					formatMessage({id: 'retainer.period_price'}),
					'-',
					formatMessage({id: 'common.projected_total_billable_value_of_service'}),
			  ];

		return [
			{
				title: formatMessage({id: titleId}),
				description: formatMessage({id: descriptionId}),
				details,
			},
		];
	} else {
		// Fixed Hours or Time and Material: Projected Total Budget Remaining
		const titleId = 'common.projected_total_budget_remaining';
		const descriptionId = 'retainer.projected_total_budget_remaining_hours_tooltip_description';
		const details =
			isFixedHours && hasRollover
				? [formatMessage({id: 'retainer.adjusted_target'}), '-', formatMessage({id: 'common.projected_total_hours'})]
				: [formatMessage({id: 'retainer.period_target'}), '-', formatMessage({id: 'common.projected_total_hours'})];

		return [
			{
				title: formatMessage({id: titleId}),
				description: formatMessage({id: descriptionId}),
				details,
			},
		];
	}
};

const createTooltip = <T,>(itemsFunction: (formatMessage: FormatMessage, props: T) => TooltipFormulaItem[]): React.FC<T> => {
	return (props: React.PropsWithChildren<T>) => {
		const {formatMessage} = useIntl();

		const items = itemsFunction(formatMessage, props as T);

		return <FinancialCategoryTooltip {...props} size={'xl'} items={items} />;
	};
};

export const PeriodHeaderTooltips = {
	NetRolloverTooltip: createTooltip(NetRolloverTooltipContent),
	PeriodPriceTargetTooltip: createTooltip(PeriodPriceTargetTooltipContent),
	AdjustedPriceTooltip: createTooltip(AdjustedPriceTooltipContent),
	AdjustedTargetTooltip: createTooltip(AdjustedTargetTooltipContent),
	ActualBillableTooltip: createTooltip(ActualBillableTooltipContent),
	ActualBudgetRemainingTooltip: createTooltip(ActualBudgetRemainingTooltipContent),
	ActualPercentageUsedTooltip: createTooltip(ActualPercentageUsedTooltipContent),
	ProjectedTotalBillableTooltip: createTooltip(ProjectedTotalBillableTooltipContent),
	ProjectedTotalBudgetRemainingTooltip: createTooltip(ProjectedTotalBudgetRemainingTooltipContent),
};
